/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { ActionDelegate, IActionParams } from "@adapptio/lib-core";
import { valueToNormalSignature } from "@adapptio/lib-shared";
import { IEditorApiService } from "../api/IEditorApiService";

/**
 * Action repository options
 */
export interface IEditorActionRepositoryOpts {
	checkTimeoutMs: number;
	delegateCacheTimeoutMs: number;
}

/**
 * Action Repository
 * Manages Action Delegates
 */
export class EditorActionRepository {
	/** Repository config */
	private config: IEditorActionRepositoryOpts;

	/** Application API client */
	private apiSvc: IEditorApiService;

	/** If action debug mode is enabled (defaults to true) */
	private debugEnabled = true;

	/** Index of existing delegate instances */
	private delegateInstances: {
		[K: string]: ActionDelegate;
	} = {};

	private reloadTimer: ReturnType<typeof setTimeout>;

	/**
	 * Repository constructor
	 *
	 * @param apiApp ApiApp Instance
	 */
	public constructor(apiSvc: IEditorApiService, config: IEditorActionRepositoryOpts) {
		this.apiSvc = apiSvc;
		this.config = config;

		this.scheduleCheck();
	}

	/**
	 * Enables debug mode
	 */
	public enableDebug(): void {
		this.debugEnabled = true;
	}

	/** Disables debug mode */
	public disableDebug(): void {
		this.debugEnabled = false;
	}

	/**
	 * Returns if debug mode is enabled
	 */
	public isDebugEnabled(): boolean {
		return this.debugEnabled;
	}

	/**
	 * Returns or creates a delegate if not exists
	 *
	 * @param actionId Action ID
	 * @param params Action params
	 * @param initialState Initial state
	 */
	private getOrCreateDelegate(actionId: string, params: IActionParams) {
		const hash = valueToNormalSignature({
			actionId: actionId,
			params: params,
			debug: this.debugEnabled
		});

		if (this.delegateInstances[hash]) {
			return this.delegateInstances[hash];
		} else {
			return (this.delegateInstances[hash] = new ActionDelegate(
				actionId,
				params,
				() =>
					this.apiSvc.invokeAction(actionId, params, {
						withTypeDescriptor: true,
						debug: this.debugEnabled
					}),
				undefined
			));
		}
	}

	/**
	 * Returns action delegate
	 *
	 * @param actionId Action ID
	 * @param params Action params
	 */
	public getDelegate(actionId: string, params: IActionParams): ActionDelegate {
		return this.getOrCreateDelegate(actionId, params);
	}

	/**
	 * Invokes reload on all delegates matching the actionId and params mask
	 *
	 * @param actionId Action ID
	 * @param paramsMask Params mask
	 * @param waitForData If to wait for delegates to reload
	 */
	public async reloadDelegates(actionId: string, paramsMask: IActionParams, waitForData: boolean): Promise<void> {
		const promises = [];

		for (const key in this.delegateInstances) {
			const delegate = this.delegateInstances[key];

			if (delegate.getActionId() !== actionId) {
				continue;
			}

			const delegateParams = delegate.getParams();

			for (const paramName in paramsMask) {
				if (paramsMask[paramName] !== undefined) {
					if (delegateParams[paramName] !== paramsMask[paramName]) {
						continue;
					}
				}
			}

			const reloadPromise = delegate.invoke(true);

			if (waitForData) {
				promises.push(reloadPromise);
			} else {
				reloadPromise.catch((err) => {
					console.warn(`[ActionRepository] Failed to reload data for action delegate '${key}':`, err)
				});
			}
		}

		try {
			await Promise.all(promises);
		} catch (err) {
			console.warn(`[ActionRepository] Failed to reload data for action delegates:`, err);
		}
	}

	/**
	 * Performs check on delegates
	 * Does reloads and disposes unused delegates
	 */
	private doCheck() {
		const now = Date.now();

		for (const k in this.delegateInstances) {
			const delegate = this.delegateInstances[k];

			if (delegate.isUsed()) {
				delegate.checkReload();
			} else {
				const lastInvoke = delegate.getLastInvocationTimestamp();
				const expired =
					!lastInvoke || lastInvoke < now - this.config.delegateCacheTimeoutMs ? true : false;

				if (expired) {
					if (window.APP_DEBUG) {
						console.debug(
							"[ActionRepository] Disposing unused action delegate '%s' with params:",
							delegate.getActionId(),
							delegate.getParams()
						);
					}

					delegate.dispose();
					delete this.delegateInstances[k];
				}
			}
		}
	}

	private checkAndSchedule = () => {
		this.reloadTimer = null;
		this.scheduleCheck();
		this.doCheck();
	}

	/**
	 * Schedules check process
	 */
	private scheduleCheck() {
		// Prevent double-schedule
		if (this.reloadTimer) {
			return;
		}

		this.reloadTimer = setTimeout(this.checkAndSchedule, this.config.checkTimeoutMs);
	}

	/**
	 * Disposes repository
	 */
	public dispose(): void {
		for (const k in this.delegateInstances) {
			this.delegateInstances[k].dispose();
		}

		this.delegateInstances = {};
	}
}
